import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiShoppingCart, FiTruck, FiShield, FiRefreshCw, FiChevronRight, FiCheck } from "react-icons/fi";
import toast from "react-hot-toast";
import { getProductBySlug, getRelatedProducts } from "../data/products";
import { useCart } from "../context/CartContext";
import { formatCurrency, formatDiscount } from "../utils/currency";
import StarRating from "../components/ui/StarRating";
import Badge from "../components/ui/Badge";
import QuantitySelector from "../components/ui/QuantitySelector";
import ProductCard from "../components/product/ProductCard";
import Button from "../components/ui/Button";

const ProductDetailPage = () => {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState("features");
  const [added, setAdded] = useState(false);

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-6xl">📦</p>
        <h2 className="text-2xl font-bold text-gray-900">Product Not Found</h2>
        <p className="text-gray-500">This product doesn't exist or has been removed.</p>
        <Link to="/shop" className="btn-primary">Back to Shop</Link>
      </div>
    );
  }

  const related = getRelatedProducts(product);
  const discount = formatDiscount(product.price, product.compareAtPrice);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAdded(true);
    toast.success(`${product.title} added to cart!`);
    setTimeout(() => setAdded(false), 2000);
  };

  const tabs = ["features", "specifications", "shipping"];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
        <Link to="/" className="hover:text-brand-600 transition-colors">Home</Link>
        <FiChevronRight size={14} />
        <Link to="/shop" className="hover:text-brand-600 transition-colors">Shop</Link>
        <FiChevronRight size={14} />
        <Link to={`/shop?category=${product.category}`} className="hover:text-brand-600 transition-colors">{product.category}</Link>
        <FiChevronRight size={14} />
        <span className="text-gray-900 font-medium line-clamp-1">{product.title}</span>
      </nav>

      {/* Main product grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
        {/* Images */}
        <div className="space-y-4">
          <motion.div
            key={activeImage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="aspect-square bg-gray-100 rounded-2xl overflow-hidden"
          >
            <img
              src={product.images[activeImage]}
              alt={product.title}
              className="w-full h-full object-cover"
            />
          </motion.div>
          <div className="flex gap-3">
            {product.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                  activeImage === i ? "border-brand-500" : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div>
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-3">
            {product.badges?.map((b) => <Badge key={b} label={b} />)}
          </div>

          <p className="text-sm text-brand-600 font-semibold uppercase tracking-wider mb-1">{product.brand} · {product.category}</p>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-snug mb-3">{product.title}</h1>

          <div className="flex items-center gap-3 mb-4">
            <StarRating rating={product.rating} reviewCount={product.reviewCount} size="md" />
            <span className="text-sm text-gray-500">SKU: {product.sku}</span>
          </div>

          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-3xl font-black text-gray-900">{formatCurrency(product.price)}</span>
            {product.compareAtPrice && (
              <span className="text-lg text-gray-400 line-through">{formatCurrency(product.compareAtPrice)}</span>
            )}
            {discount && (
              <span className="bg-red-100 text-red-700 text-sm font-bold px-2.5 py-0.5 rounded-lg">
                Save {discount}%
              </span>
            )}
          </div>

          {product.compareAtPrice && (
            <p className="text-emerald-600 text-sm font-medium mb-4">
              You save {formatCurrency(product.compareAtPrice - product.price)}
            </p>
          )}

          <p className="text-gray-600 leading-relaxed mb-6">{product.shortDescription}</p>

          {/* Stock */}
          <div className={`inline-flex items-center gap-2 text-sm font-medium mb-6 ${product.stock > 20 ? "text-emerald-600" : product.stock > 0 ? "text-amber-600" : "text-red-600"}`}>
            <span className={`w-2 h-2 rounded-full ${product.stock > 20 ? "bg-emerald-500" : product.stock > 0 ? "bg-amber-500" : "bg-red-500"}`} />
            {product.stock > 20 ? "In Stock" : product.stock > 0 ? `Only ${product.stock} left!` : "Out of Stock"}
          </div>

          {/* Quantity */}
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Quantity</p>
            <QuantitySelector
              quantity={quantity}
              onChange={setQuantity}
              max={product.stock}
              disabled={product.stock === 0}
            />
          </div>

          {/* Add to cart */}
          <div className="flex gap-3 mb-8">
            <Button
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className="flex-1"
            >
              {added ? <><FiCheck size={18} /> Added!</> : <><FiShoppingCart size={18} /> Add to Cart</>}
            </Button>
            <Link to="/cart" className="btn-secondary px-5">
              View Cart
            </Link>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-3 gap-3 p-4 bg-gray-50 rounded-2xl">
            {[
              { icon: FiTruck, label: "Free Shipping", sub: "Orders $99+" },
              { icon: FiShield, label: "1-Year Warranty", sub: "All products" },
              { icon: FiRefreshCw, label: "30-Day Return", sub: "Easy & free" },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className="flex flex-col items-center text-center gap-1">
                <Icon size={18} className="text-brand-600" />
                <p className="text-xs font-semibold text-gray-800">{label}</p>
                <p className="text-xs text-gray-500">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-16">
        <div className="flex border-b border-gray-200 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-semibold capitalize transition-colors border-b-2 -mb-px ${
                activeTab === tab ? "border-brand-600 text-brand-600" : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === "features" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid sm:grid-cols-2 gap-3">
            {product.features.map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <FiCheck size={16} className="text-brand-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{f}</span>
              </div>
            ))}
          </motion.div>
        )}

        {activeTab === "specifications" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="overflow-x-auto">
            <table className="w-full">
              <tbody>
                {Object.entries(product.specifications).map(([key, value], i) => (
                  <tr key={key} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-700 w-1/3 rounded-l-lg">{key}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 rounded-r-lg">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}

        {activeTab === "shipping" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="prose prose-sm max-w-none">
            <div className="p-6 bg-gray-50 rounded-2xl space-y-4">
              <div className="flex items-start gap-3">
                <FiTruck size={20} className="text-brand-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Shipping</p>
                  <p className="text-gray-600 text-sm">{product.shippingInfo}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FiRefreshCw size={20} className="text-brand-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Returns</p>
                  <p className="text-gray-600 text-sm">30-day hassle-free returns. Items must be in original packaging and unused condition.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FiShield size={20} className="text-brand-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Warranty</p>
                  <p className="text-gray-600 text-sm">All StoreOnline products come with a 1-year manufacturer warranty against defects.</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">You May Also Like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
